import { useEffect } from 'react';
import { useSnackbar } from '@planitgeo/treeplotter-ui-library';
import { useWorkSubmissionSyncRunner } from 'common/infrastructure/work-task/workSubmissionRunner';
import {
  subscribeWorkSubmissionFailures,
  WorkSubmissionFailure,
  WorkSubmissionFailureStep,
} from 'common/infrastructure/work-task/workSubmissionOutbox';
import { useLocales } from 'locales';

// Translation keys naming the step a submission permanently failed at (for the toast).
const FAILURE_STEP_KEYS: Record<WorkSubmissionFailureStep, string> = {
  host: 'work.create.failed_step.host',
  asset: 'work.create.failed_step.asset',
  work: 'work.create.failed_step.work',
  photos: 'work.create.failed_step.photos',
};

/**
 * Headless, app-wide runner for the work-submission outbox. Mounted once near the app
 * root so queued work (from any of the create-work flows) is driven — in dependency order
 * and across restarts/reconnects — to created + photos-attached regardless of which route
 * is open. Also surfaces a toast (naming the failed step + tp2 asset id) when a submission
 * is permanently given up. Renders nothing; the Work view reads
 * {@link useIsWorkSubmissionSyncing} for its spinner/banner.
 */
export default function WorkTaskSyncRunner(): null {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();

  useWorkSubmissionSyncRunner();

  useEffect(
    () =>
      subscribeWorkSubmissionFailures((failure: WorkSubmissionFailure) => {
        enqueueSnackbar(
          translate('work.create.submit_failed', {
            step: translate(FAILURE_STEP_KEYS[failure.step]),
            assetId: failure.tp2AssetId ?? '—',
          }),
          { variant: 'error' }
        );
      }),
    [enqueueSnackbar, translate]
  );

  return null;
}
